import React, { useMemo, forwardRef, useRef, InputHTMLAttributes } from 'react';
import tw, { css, theme } from 'twin.macro';
import { motion, AnimatePresence } from 'framer-motion';

const InputContainer = tw.div`relative w-full`;
const StyledLabel = tw.label`text-sm text-accent-2 font-semibold px-1 truncate`;
// TODO: Sync with design team on variable for #D6D6FF
const StyledInputWrapper = tw.div`w-full h-[40px] text-text-and-icon px-3 py-2 outline-none bg-[#D6D6FF] border border-accent-1 rounded-md text-sm focus-within:border-accent-2 flex`;
const IconWrapper = tw.div`flex items-center justify-center`;
const HelperTextContainer = tw.div`mt-1 text-xs font-normal`;
const AnimatedHelperTextContainer = motion.div;

const errorInputStyles = css`
  ${tw`text-error-text-icon placeholder:text-error-text-icon focus:placeholder:text-error-text-icon `}
`;

const errorWrapperStyles = css`
  ${tw`border border-error-text-icon focus-within:border-error-text-icon focus-within:text-error-text-icon`}
`;

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  renderIcon?: () => JSX.Element;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const labelRef = useRef<HTMLLabelElement>(null);

  const {
    label,
    name,
    error,
    helperText,
    onChange,
    onFocus,
    onBlur,
    renderIcon = null,
    ...inputProps
  } = props;

  const hasError = useMemo(() => !!error, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (typeof onChange === 'function') {
      onChange(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  };

  return (
    <InputContainer>
      {!!label && (
        <StyledLabel htmlFor={name} ref={labelRef}>
          {label}
        </StyledLabel>
      )}

      <StyledInputWrapper css={[hasError && errorWrapperStyles]}>
        <input
          tw='w-full h-full outline-none bg-transparent text-text-and-icon autofill:text-text-and-icon! peer-autofill:bg-transparent! peer-aria-selected:bg-transparent! placeholder:text-accent-muted focus:placeholder:text-accent-2! focus:text-text-and-icon'
          {...inputProps}
          ref={ref}
          name={name}
          onChange={(e) => {
            handleChange(e as React.ChangeEvent<HTMLInputElement>);
          }}
          onFocus={(e) => {
            handleFocus(e as React.FocusEvent<HTMLInputElement>);
          }}
          onBlur={(e) => {
            handleBlur(e as React.FocusEvent<HTMLInputElement>);
          }}
          css={[hasError && errorInputStyles]}
        />
        {renderIcon && (
          <IconWrapper data-testid='input-icon' tw='top-1/2 pl-1'>
            {renderIcon()}
          </IconWrapper>
        )}
      </StyledInputWrapper>
      <AnimatePresence mode='wait'>
        <AnimatedHelperTextContainer layout>
          <HelperTextContainer
            css={css({
              color: hasError ? theme`colors.error-text-icon` : theme`textColor.accent-muted`,
            })}>
            {hasError ? error : helperText}
          </HelperTextContainer>
        </AnimatedHelperTextContainer>
      </AnimatePresence>
    </InputContainer>
  );
});

Input.displayName = 'Input';

export { Input };
